import { useState, useEffect, useCallback, useRef } from 'react';
import { leadsAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const CHANNELS = ['CALL', 'WHATSAPP', 'EMAIL', 'WECHAT', 'SKYPE', 'LINKEDIN', 'MEETING'];
const OUTCOMES = [
  ['DID_NOT_PICK', 'Did not pick (DNP)'], ['NOT_REACHABLE', 'Not reachable'], ['INVALID_NUMBER', 'Invalid number'],
  ['DISCONNECTED_AFTER_INTRO', 'Disconnected after intro'], ['LANGUAGE_BARRIER', 'Language barrier'], ['MESSAGE_NO_REPLY', 'Message sent, no reply'],
  ['CALL_BACK', 'Asked to call back'], ['ASKED_EMAIL_OFFERS', 'Asked for email or offers'], ['SHARED_REQUIREMENT', 'Shared requirement'],
  ['SHARED_STOCK_LIST', 'Shared stock list'], ['CONNECTED_MESSAGING', 'Connected on WhatsApp/WeChat/Skype'],
  ['NO_REQUIREMENT_NOW', 'Interested, no requirement now'],
  ['NO_IMPORT_EXPORT', 'Does not import or export'], ['SMALL_RETAILER', 'Small retailer'], ['PERSONAL_USE', 'Personal use'],
  ['NOT_OUR_INDUSTRY', 'Not in our industry'], ['DEALS_WITH_COMPETITOR', 'Deals with a competitor'],
];
const CLOSE_REASONS = ['JUNK_INVALID', 'UNREACHABLE', 'NO_FIT', 'NOT_INTERESTED', 'COMPETITOR'];

export default function LeadDetailModal({ leadId, caps, onClose }) {
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [channel, setChannel] = useState('');
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState('');
  const [rating, setRating] = useState('');
  const [productInterests, setProductInterests] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [convertMode, setConvertMode] = useState('LINK');
  const [convertTargetType, setConvertTargetType] = useState('CUSTOMER');
  const [convertLinkName, setConvertLinkName] = useState('');
  const [convertSelected, setConvertSelected] = useState(null);
  const [convertResults, setConvertResults] = useState([]);
  const [convertMatches, setConvertMatches] = useState([]);
  const [converting, setConverting] = useState(false);
  const convertJustPickedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const [l, a] = await Promise.all([leadsAPI.get(leadId), leadsAPI.getActivities(leadId)]);
      setLead(l.data);
      setActivities(a.data);
    } catch (e) {
      toast.error('Failed to load lead');
      onClose(false);
    }
  }, [leadId, onClose]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    leadsAPI.getSettings().then(({ data }) => setProductOptions(data.product_interests || [])).catch(() => {});
  }, []);

  const toggleProductInterest = (p) => {
    setProductInterests(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p]);
  };

  const submitActivity = async () => {
    setSaving(true);
    try {
      const payload = { channel, outcome, note, next_follow_up: nextFollowUp || null };
      if (rating) payload.rating = parseInt(rating, 10);
      if (rating && parseInt(rating, 10) >= 3) payload.product_interests = productInterests;
      const { data } = await leadsAPI.logActivity(leadId, payload);
      toast.success('Activity logged');
      setChannel(''); setOutcome(''); setNote(''); setNextFollowUp(''); setRating(''); setProductInterests([]);
      setChanged(true);
      if (data.suggest_close) {
        toast.info(`This lead looks like a good fit to close (${data.suggest_close}) — use the Close button if you agree.`, { duration: 8000 });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log activity');
    } finally { setSaving(false); }
  };

  const doClose = async () => {
    const reason = window.prompt(`Close reason? One of: ${CLOSE_REASONS.join(', ')}`);
    if (!reason) return;
    try {
      await leadsAPI.close(leadId, { reason });
      toast.success('Lead closed');
      setChanged(true);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Close failed'); }
  };

  const doReopen = async () => {
    const d = window.prompt('Next follow-up date (YYYY-MM-DD)?');
    if (!d) return;
    try {
      await leadsAPI.reopen(leadId, { next_follow_up: d });
      toast.success('Lead reopened');
      setChanged(true);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reopen failed'); }
  };

  const searchConvertTargets = (q) => {
    setConvertLinkName(q);
    setConvertSelected(null);
  };

  const pickConvertTarget = (item) => {
    convertJustPickedRef.current = true;
    setConvertSelected(item);
    setConvertLinkName(item.name);
    setConvertResults([]);
  };

  useEffect(() => {
    if (convertJustPickedRef.current) { convertJustPickedRef.current = false; return; }
    if (!convertLinkName) { setConvertResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await leadsAPI.convertSearch(leadId, convertTargetType, convertLinkName);
        setConvertResults(data);
      } catch (e) { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertLinkName, convertTargetType]);

  const [forceNewReason, setForceNewReason] = useState('');

  const doConvert = async (forceNew = false) => {
    setConverting(true);
    toast.dismiss();
    try {
      const payload = { target_type: convertTargetType };
      if (convertMode === 'LINK' && convertSelected) payload.link_to = { kind: convertSelected.kind, crm_id: convertSelected.crm_id };
      if (forceNew) { payload.force_new = true; payload.force_new_reason = forceNewReason; }
      await leadsAPI.convert(leadId, payload);
      toast.success(`Lead converted to a ${convertTargetType.toLowerCase()}`);
      setShowConvert(false);
      setConvertMatches([]);
      setForceNewReason('');
      setChanged(true);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.matches) {
        setConvertMatches(detail.matches);
        toast.error('Matching CRM record(s) already exist — link to one or create anyway');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Conversion failed');
      }
    }
    finally { setConverting(false); }
  };

  if (!lead) return null;
  const isClosed = lead.status === 'CLOSED';
  const isConverted = lead.status === 'CONVERTED';
  const canConvertNow = lead.status === 'CONTACTED' || lead.status === 'NURTURING';

  return (
    <Dialog open onOpenChange={() => onClose(changed)}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lead-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{lead.company} <Badge variant="outline">{lead.code}</Badge> <Badge>{lead.status}</Badge></DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground">Contact:</span> {lead.contact_name || '—'}</div>
            <div><span className="text-muted-foreground">Email:</span> {lead.email || '—'}</div>
            <div><span className="text-muted-foreground">Phone:</span> {lead.phone || '—'}</div>
            <div><span className="text-muted-foreground">Mobile:</span> {lead.mobile || '—'}</div>
            <div><span className="text-muted-foreground">Country:</span> {lead.country}</div>
            <div><span className="text-muted-foreground">Owner:</span> {lead.owner_name || 'Unassigned'}</div>
            <div><span className="text-muted-foreground">Rating:</span> {lead.rating ? `★${lead.rating}` : '—'}</div>
            <div><span className="text-muted-foreground">Next follow-up:</span> {lead.next_follow_up || '—'}</div>
          </div>

          {!isClosed && !isConverted && caps.can_work && (
            <div className="border rounded-sm p-3 space-y-2" data-testid="lead-log-activity-form">
              <h4 className="font-semibold text-sm">Log activity</h4>
              <div className="grid grid-cols-2 gap-2">
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger className="h-9" data-testid="activity-channel-select"><SelectValue placeholder="Channel" /></SelectTrigger>
                  <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={outcome} onValueChange={setOutcome}>
                  <SelectTrigger className="h-9" data-testid="activity-outcome-select"><SelectValue placeholder="Outcome" /></SelectTrigger>
                  <SelectContent>{OUTCOMES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Textarea placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="activity-note-input" />
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={nextFollowUp} onChange={(e) => setNextFollowUp(e.target.value)} data-testid="activity-next-follow-up-input" />
                <Select value={rating} onValueChange={setRating}>
                  <SelectTrigger className="h-9" data-testid="activity-rating-select"><SelectValue placeholder="Rating" /></SelectTrigger>
                  <SelectContent>{[1, 2, 3, 4, 5].map(r => <SelectItem key={r} value={String(r)}>{'★'.repeat(r)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {rating && parseInt(rating, 10) >= 3 && (
                <div className="space-y-1" data-testid="activity-product-interests">
                  <p className="text-xs text-muted-foreground">Product interest (required for rating 3+)</p>
                  <div className="flex flex-wrap gap-1">
                    {productOptions.map(p => (
                      <Badge key={p.code} variant={productInterests.includes(p.code) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleProductInterest(p.code)} data-testid={`activity-product-interest-${p.code}`}>{p.label}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={submitActivity} disabled={saving || !channel || !outcome || !nextFollowUp || (rating && parseInt(rating, 10) >= 3 && productInterests.length === 0)} data-testid="activity-submit-btn">Save activity</Button>
            </div>
          )}

          <div className="flex gap-2">
            {!isClosed && !isConverted && <Button variant="destructive" size="sm" onClick={doClose} data-testid="lead-close-btn">Close lead</Button>}
            {isClosed && <Button variant="outline" size="sm" onClick={doReopen} data-testid="lead-reopen-btn">Reopen</Button>}
            {!isClosed && !isConverted && caps.conversion_enabled && (
              <span title={!canConvertNow ? 'Lead must be Contacted or Nurturing to convert' : ''}>
                <Button variant="outline" size="sm" onClick={() => setShowConvert(s => !s)} disabled={!canConvertNow} data-testid="lead-convert-toggle-btn">Convert</Button>
              </span>
            )}
          </div>

          {showConvert && (
            <div className="border rounded-sm p-3 space-y-2" data-testid="lead-convert-form">
              <h4 className="font-semibold text-sm">Convert lead</h4>
              <div className="grid grid-cols-2 gap-2">
                <Select value={convertTargetType} onValueChange={(v) => { setConvertTargetType(v); setConvertResults([]); setConvertMatches([]); setConvertSelected(null); }}>
                  <SelectTrigger className="h-9" data-testid="convert-target-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CUSTOMER">Customer</SelectItem>
                    <SelectItem value="VENDOR">Vendor</SelectItem>
                    <SelectItem value="BOTH">Both</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={convertMode} onValueChange={(v) => { setConvertMode(v); setConvertMatches([]); }}>
                  <SelectTrigger className="h-9" data-testid="convert-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LINK">Link to existing</SelectItem>
                    <SelectItem value="NEW">Create new in Zoho CRM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {convertMode === 'LINK' && (
                <div className="relative">
                  <Input placeholder="Search existing by name" value={convertLinkName} onChange={(e) => searchConvertTargets(e.target.value)} data-testid="convert-link-search-input" />
                  {convertResults.length > 0 && (
                    <div className="border rounded-sm mt-1 max-h-32 overflow-y-auto">
                      {convertResults.map(r => (
                        <div key={`${r.kind}-${r.crm_id}`} className="px-2 py-1 text-sm cursor-pointer hover:bg-muted" onClick={() => pickConvertTarget(r)} data-testid={`convert-link-option-${r.crm_id}`}>{r.name} <Badge variant="outline" className="ml-1 text-[10px]">{r.kind}</Badge></div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {convertMatches.length > 0 && (
                <div className="border border-amber-400 rounded-sm p-2 space-y-1 bg-amber-50" data-testid="convert-matches-warning">
                  <p className="text-xs font-medium text-amber-800">Possible existing CRM match(es) found:</p>
                  {convertMatches.map(m => (
                    <div key={`${m.kind}-${m.crm_id}`} className="text-xs flex items-center justify-between gap-2">
                      <span>{m.name || m.lead_code || m.crm_id} <Badge variant="outline" className="text-[10px]">{m.kind}</Badge></span>
                      {m.kind !== 'CONVERTED_LEAD' && (
                        <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => { setConvertMode('LINK'); pickConvertTarget(m); setConvertMatches([]); }} data-testid={`convert-match-link-${m.crm_id}`}>Link to this</Button>
                      )}
                    </div>
                  ))}
                  {caps.can_admin && (
                    <div className="space-y-1 pt-1">
                      <Input value={forceNewReason} onChange={e => setForceNewReason(e.target.value)} placeholder="Reason for creating a new CRM record instead of linking (required)" className="h-7 text-xs" data-testid="convert-force-new-reason-input" />
                      <Button size="sm" variant="destructive" className="h-6 text-xs" disabled={!forceNewReason.trim()} onClick={() => doConvert(true)} data-testid="convert-force-new-btn">Create new anyway</Button>
                    </div>
                  )}
                </div>
              )}
              <Button onClick={() => doConvert(false)} disabled={converting || (convertMode === 'LINK' && !convertSelected)} data-testid="lead-convert-submit-btn">
                Convert to {convertTargetType.toLowerCase()}
              </Button>
            </div>
          )}

          {lead.converted_to && (
            <div className="text-sm text-muted-foreground" data-testid="lead-converted-to-info">
              Converted to {lead.converted_to.type?.toLowerCase()} "{lead.converted_to.name}" ({lead.converted_to.mode === 'NEW' ? 'created in Zoho CRM' : 'linked to existing'})
            </div>
          )}

          <div>
            <h4 className="font-semibold text-sm mb-2">Timeline</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="lead-timeline">
              {activities.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet</p> : activities.map(a => (
                <div key={a.id} className="text-sm border-b pb-2" data-testid={`lead-activity-${a.id}`}>
                  <div className="flex items-center gap-2"><Badge variant="outline">{a.channel}</Badge><span>{a.outcome}</span>{a.source === 'ZOHO_IMPORT' && <Badge variant="secondary">from Zoho</Badge>}</div>
                  {a.note && <div className="text-muted-foreground mt-1">{a.note}</div>}
                  <div className="text-xs text-muted-foreground mt-1">{a.by_name} · {new Date(a.at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
